// components/ui/Accordion.tsx — Collapsible sections for profile and settings

import * as RadixAccordion from '@radix-ui/react-accordion';
import type { ReactNode } from 'react';
import styles from './Accordion.module.css';

export interface AccordionSection {
  id: string;
  trigger: ReactNode;
  content: ReactNode;
}

interface AccordionProps {
  sections: AccordionSection[];
  type?: 'single' | 'multiple';
  defaultOpen?: string | string[];
  collapsible?: boolean;
}

/**
 * Accordion — collapsible sections for profile view and settings panels.
 * Wraps Radix Accordion with animated slide-down content.
 */
export function Accordion({
  sections,
  type = 'single',
  defaultOpen,
  collapsible = true,
}: AccordionProps) {
  if (type === 'multiple') {
    return (
      <RadixAccordion.Root
        type="multiple"
        {...(defaultOpen !== undefined
          ? { defaultValue: Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen] }
          : {})}
      >
        {sections.map((section) => (
          <AccordionItem key={section.id} section={section} />
        ))}
      </RadixAccordion.Root>
    );
  }

  return (
    <RadixAccordion.Root
      type="single"
      {...(typeof defaultOpen === 'string' ? { defaultValue: defaultOpen } : {})}
      collapsible={collapsible}
    >
      {sections.map((section) => (
        <AccordionItem key={section.id} section={section} />
      ))}
    </RadixAccordion.Root>
  );
}

function AccordionItem({ section }: { section: AccordionSection }) {
  return (
    <RadixAccordion.Item className={styles.item} value={section.id}>
      <RadixAccordion.Header>
        <RadixAccordion.Trigger className={styles.trigger}>
          {section.trigger}
          <span className={styles.chevron} aria-hidden="true">▼</span>
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
      <RadixAccordion.Content className={styles.content}>
        <div className={styles.contentInner}>
          {section.content}
        </div>
      </RadixAccordion.Content>
    </RadixAccordion.Item>
  );
}
